import DynamicButtonLink from "@shared/components/DynamicButtonLink";
import { ArrowIcon } from "@shared/components/ui/Icon";
import React from "react";
import {
  RecordingItemData,
  RecordingsSectionData,
} from "../../types/home.types";

// Extended interface for RecordingItemData with onButtonClick handler
interface RecordingItemProps extends RecordingItemData {}

// Props interface for the RecordingsSection component
export interface RecordingsSectionProps
  extends Omit<
    RecordingsSectionData,
    "id" | "createdAt" | "updatedAt" | "recordings"
  > {
  recordings?: RecordingItemProps[];
  /** Spans the whole grid row instead of one column. */
  fullWidth?: boolean;
}

const RecordingItem: React.FC<RecordingItemProps> = ({
  month,
  description = "",
  buttonText = "Watch now",
  buttonLink = "#",
  buttonTarget = "_self",
}) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700/60 last:border-0">
      <div>
        <h4 className="font-medium text-gray-800 dark:text-gray-100">
          {month}
        </h4>
      </div>
      <DynamicButtonLink
        buttonText={buttonText}
        buttonLink={buttonLink}
        buttonTarget={buttonTarget}
        variant="outline"
        size="sm"
      />
    </div>
  );
};

const RecordingsSection: React.FC<RecordingsSectionProps> = ({
  title = "",
  recordings = [],
  viewAllText = "",
  viewAllLink = "#",
  viewAllTarget = "_self",
  fullWidth = false,
}) => {
  if (recordings.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden p-5">
        <h2 className="font-madefor font-semibold text-gray-800 dark:text-gray-100">
          {title}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-3">
          No recordings available.
        </p>
      </div>
    );
  }

  const featuredRecording = recordings[0];

  const remainingRecordings = recordings.slice(1);

  const header = (
    <header className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
      <h2 className="font-madefor font-semibold text-gray-800 dark:text-gray-100">
        {title}
      </h2>
    </header>
  );

  const viewAll = (
    <DynamicButtonLink
      buttonText={viewAllText}
      buttonLink={viewAllLink}
      buttonTarget={viewAllTarget}
      variant="link"
      icon={<ArrowIcon />}
    />
  );

  // Across the full row the single column would leave most of the card empty,
  // so the featured recording sits beside a two-column list.
  if (fullWidth) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden">
        {header}

        <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:border-r lg:pr-6 border-gray-200 dark:border-gray-700/60">
            {featuredRecording.imagePath && (
              <img
                src={featuredRecording.imagePath}
                alt={`${featuredRecording.month} Recording`}
                className="w-full h-[160px] object-cover rounded-lg mb-3"
              />
            )}
            <h3 className="text-2xl text-gray-800 dark:text-gray-100 mb-1">
              {featuredRecording.month}
            </h3>
            {featuredRecording.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                {featuredRecording.description}
              </p>
            )}
            <DynamicButtonLink
              buttonText={featuredRecording.buttonText}
              buttonLink={featuredRecording.buttonLink}
              buttonTarget={featuredRecording.buttonTarget}
              variant="outline"
              size="sm"
            />
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {remainingRecordings.map((recording, index) => (
              <RecordingItem
                key={index}
                month={recording.month}
                description={recording.description}
                buttonText={recording.buttonText}
                buttonLink={recording.buttonLink}
                buttonTarget={recording.buttonTarget}
              />
            ))}
          </div>
        </div>

        <div className="px-5 pb-5 text-right">{viewAll}</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden min-h-[542px] flex flex-col">
      <header className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
        <h2 className="font-madefor font-semibold text-gray-800 dark:text-gray-100">
          {title}
        </h2>
      </header>

      {/* Featured recording with image */}
      <div className="px-5 border-b border-gray-200 dark:border-gray-700/60">
        <div className="flex flex-row ">
          <div className="w-2/3 mb-4 md:mb-0 md:pr-4">
            <h3 className="text-2xl text-gray-800 dark:text-gray-100 mb-1">
              {featuredRecording.month}
            </h3>
            {featuredRecording.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                {featuredRecording.description}
              </p>
            )}
            <DynamicButtonLink
              buttonText={featuredRecording.buttonText}
              buttonLink={featuredRecording.buttonLink}
              buttonTarget={featuredRecording.buttonTarget}
              variant="outline"
              size="sm"
            />
          </div>
          {featuredRecording.imagePath && (
            <div className="w-1/2">
              <img
                src={featuredRecording.imagePath}
                alt={`${featuredRecording.month} Recording`}
                className="w-full h-[160px] pt-4 object-cover"
              />
            </div>
          )}
        </div>
      </div>

      {/* List of recordings */}
      <div className="p-5 flex flex-col justify-between h-full flex-1">
        <div className="max-h-[257px] overflow-y-hidden">
          {remainingRecordings.map((recording, index) => (
            <RecordingItem
              key={index}
              month={recording.month}
              description={recording.description}
              buttonText={recording.buttonText}
              buttonLink={recording.buttonLink}
              buttonTarget={recording.buttonTarget}
            />
          ))}
        </div>

        {/* View all link */}
        <div className="mt-4 text-right">
          <DynamicButtonLink
            buttonText={viewAllText}
            buttonLink={viewAllLink}
            buttonTarget={viewAllTarget}
            variant="link"
            icon={<ArrowIcon />}
          />
        </div>
      </div>
    </div>
  );
};

export default RecordingsSection;
